import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { ApiClient } from '@/api/client'
import { useApiClient } from '@/api/ApiClientProvider'
import type { FormatOptions } from '@/design/format'
import { useCurrentTenant } from '@/lib/access'
import { drillDownHref, type DrillDown } from '@/lib/drilldown'
import type { GlobalFilters } from '@/lib/filters'
import { filtersKey, useGlobalFilters } from '@/lib/useGlobalFilters'

/** One Market Intelligence query, keyed by the filters in the URL, like the Overview's. */
export function useMarketQuery<T>(
  name: string,
  fetch: (client: ApiClient, filters: GlobalFilters) => Promise<T>,
  extraKey: readonly unknown[] = [],
) {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  return useQuery({
    queryKey: ['market', name, client.kind, filtersKey(filters), ...extraKey],
    queryFn: () => fetch(client, filters),
    placeholderData: keepPreviousData,
  })
}

/** Builds a drill-down link that carries the current filters. */
export function useDrillDown() {
  const { filters } = useGlobalFilters()
  return (target: DrillDown) => drillDownHref(target, filters)
}

/** The tenant's currency and locale, for every money figure. */
export function useMoney(): FormatOptions {
  const tenant = useCurrentTenant()
  return { currency: tenant.data?.currency ?? 'GBP', locale: tenant.data?.locale ?? 'en-GB' }
}

/**
 * Refetches every Market Intelligence query after a manual add or CSV
 * import, so the list, activity feed and summaries a viewer just changed
 * reflect it immediately.
 */
export function useInvalidateMarket() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['market'] })
}

/** Wires a market mutation to refetch every market query on success, so a page reflects a change immediately. */
function useMarketMutation<TInput, TOutput>(mutationFn: (client: ApiClient, input: TInput) => Promise<TOutput>) {
  const client = useApiClient()
  const invalidate = useInvalidateMarket()
  return useMutation({
    mutationFn: (input: TInput) => mutationFn(client, input),
    onSuccess: () => invalidate(),
  })
}

export function useAddCompetitor() {
  return useMarketMutation((client, input: Parameters<ApiClient['addMarketCompetitor']>[0]) => client.addMarketCompetitor(input))
}

export function useImportCompetitorsCsv() {
  return useMarketMutation((client, csv: string) => client.importMarketCompetitorsCsv(csv))
}

export function useAddTrackedCompetition() {
  return useMarketMutation((client, input: Parameters<ApiClient['addTrackedCompetition']>[0]) => client.addTrackedCompetition(input))
}

export function useImportTrackedCompetitionsCsv() {
  return useMarketMutation((client, csv: string) => client.importTrackedCompetitionsCsv(csv))
}

/** A form's field values, with a per-field setter and a reset back to `initial`. Shared by the market add-forms. */
export function useFormFields<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState(initial)
  function update<K extends keyof T>(key: K, value: T[K]) {
    setValues((current) => ({ ...current, [key]: value }))
  }
  function reset(next: T = initial) {
    setValues(next)
  }
  return { values, update, reset }
}
